export function generateMarketInsightsSignStackChartData(numPoints: number) {
    const data = [];
    let currentLineValue = 0; // Initialize the black line value

    for (let i = 0; i < numPoints; i++) {
        // Generate lineValue with a random walk and tendency to return to center
        let lineChange = (Math.random() - 0.5) * 300; // Reduced from 500 to 300 for less extreme changes
        lineChange -= currentLineValue * 0.15; // Increased from 0.1 to 0.15 for stronger mean reversion

        currentLineValue += lineChange;

        // Clamp lineValue to a reasonable range
        currentLineValue = Math.min(4000, Math.max(-4000, currentLineValue)); // Reduced from 5000 to 4000

        let pv = 0; // Positive value for red bars
        let cnt = 0; // Negative value for blue bars

        // Determine bar heights based on currentLineValue
        if (currentLineValue >= 0) {
            // Predominantly positive bars when lineValue is positive
            pv = Math.floor(Math.random() * (currentLineValue / 6 + 80) + 40); // Reduced multipliers and base values
            if (Math.random() < 0.08) { // Reduced chance from 0.1 to 0.08
                cnt = -(Math.floor(Math.random() * 80) + 8); // Reduced from 100+10 to 80+8
            }
        } else {
            // Predominantly negative bars when lineValue is negative
            cnt = -(Math.floor(Math.random() * (Math.abs(currentLineValue) / 6 + 80) + 40)); // Reduced multipliers and base values
            if (Math.random() < 0.08) { // Reduced chance from 0.1 to 0.08
                pv = Math.floor(Math.random() * 80) + 8; // Reduced from 100+10 to 80+8
            }
        }

        // Add additional random fluctuation to bars to make them look more varied
        pv += Math.floor(Math.random() * 150 - 75); // Reduced from 200-100 to 150-75
        cnt -= Math.floor(Math.random() * 150 - 75); // Reduced from 200-100 to 150-75

        // Ensure pv is non-negative and cnt is non-positive
        pv = Math.max(0, pv);
        cnt = Math.min(0, cnt);

        data.push({
            name: `Point ${i + 1}`,
            pv: pv * 60,
            cnt: cnt * 60,
            lineValue: Math.floor(currentLineValue) * 60 // The black line value
        });
    }
    return data;
}

interface MarketInsightPoint {
    name: string;
    uv: number;
    pv: number;
}

export function generateLMPData(): MarketInsightPoint[] {
    const data: MarketInsightPoint[] = [];
    const totalPoints = 24 * 12; // Total points generated (288 points) - corresponds to original structure

    let currentUVValue = Math.floor(Math.random() * 50) + 10; // Initial UV value between 10 and 60
    let uvStepDuration = Math.floor(Math.random() * 10) + 5; // UV step duration between 5 and 14 points
    let uvStepCounter = 0;
    let lastPvValue = currentUVValue; // Initialize lastPvValue with the current UV

    for (let i = 0; i < totalPoints; i++) {
        // Update UV value if step duration is met
        if (uvStepCounter >= uvStepDuration) {
            currentUVValue = Math.floor(Math.random() * 50) + 10; // New UV value between 10 and 60
            uvStepDuration = Math.floor(Math.random() * 10) + 5; // New step duration
            uvStepCounter = 0;
        }

        // PV: Base around lastPvValue, add smaller noise and occasional large spikes
        let pvChange = (Math.random() * 20) - 10; // Smaller random change +/- 10
        let pvValue = lastPvValue + pvChange;

        // Introduce occasional very large spikes for PV
        if (Math.random() < 0.05) { // 5% chance of a significant positive spike
            pvValue += Math.random() * 100; // Add a large positive spike
        } else if (Math.random() < 0.02) { // 2% chance of a significant negative spike
            pvValue -= Math.random() * 80; // Add a large negative spike
        }

        // Keep PV somewhat aligned with UV but allow it to deviate
        // We can add a "pull" towards UV but keep it subtle
        const uvInfluence = (currentUVValue - pvValue) * 0.1; // 10% pull towards UV
        pvValue += uvInfluence;

        // Ensure PV is not too low (e.g., non-negative, as per image)
        pvValue = Math.max(0, pvValue);

        data.push({
            name: `Point ${i + 1}`,
            uv: Math.floor(currentUVValue),
            pv: Math.floor(pvValue),
        });

        lastPvValue = pvValue; // Update lastPvValue for the next iteration
        uvStepCounter++;
    }

    return data;
}

export function generateLMPData1(): MarketInsightPoint[] {
    const data: MarketInsightPoint[] = [];
    const totalPoints = 24 * 12; // 288 points

    let currentUVValue = Math.floor(Math.random() * 50) + 10;
    let uvStepDuration = Math.floor(Math.random() * 10) + 5;
    let uvStepCounter = 0;
    let lastPvValue = currentUVValue;

    for (let i = 0; i < totalPoints; i++) {
        if (uvStepCounter >= uvStepDuration) {
            currentUVValue = Math.floor(Math.random() * 50) + 10;
            uvStepDuration = Math.floor(Math.random() * 10) + 5;
            uvStepCounter = 0;
        }

        let pvChange = (Math.random() * 20) - 10;
        let pvValue = lastPvValue + pvChange;

        if (Math.random() < 0.05) {
            pvValue += Math.random() * 100;
        } else if (Math.random() < 0.02) {
            pvValue -= Math.random() * 80;
        }

        const uvInfluence = (currentUVValue - pvValue) * 0.1;
        pvValue += uvInfluence;
        pvValue = Math.max(0, pvValue);

        data.push({
            name: `Point ${i + 1}`,
            uv: Math.floor(currentUVValue),
            pv: Math.floor(pvValue),
        });

        lastPvValue = pvValue;
        uvStepCounter++;
    }

    return data;
}

export function generateLMPData2(): MarketInsightPoint[] {
    const data: MarketInsightPoint[] = [];
    const totalPoints = 24 * 12;

    let currentUVValue = Math.floor(Math.random() * 30) + 20;
    let uvStepDuration = Math.floor(Math.random() * 15) + 10;
    let uvStepCounter = 0;
    let lastPvValue = currentUVValue;

    for (let i = 0; i < totalPoints; i++) {
        if (uvStepCounter >= uvStepDuration) {
            currentUVValue = Math.floor(Math.random() * 30) + 20;
            uvStepDuration = Math.floor(Math.random() * 15) + 10;
            uvStepCounter = 0;
        }

        let pvChange = (Math.random() * 15) - 7.5;
        let pvValue = lastPvValue + pvChange;

        if (Math.random() < 0.03) {
            pvValue += Math.random() * 150;
        } else if (Math.random() < 0.01) {
            pvValue -= Math.random() * 120;
        }

        const uvInfluence = (currentUVValue - pvValue) * 0.15;
        pvValue += uvInfluence;
        pvValue = Math.max(0, pvValue);

        data.push({
            name: `Point ${i + 1}`,
            uv: Math.floor(currentUVValue),
            pv: Math.floor(pvValue),
        });

        lastPvValue = pvValue;
        uvStepCounter++;
    }

    return data;
}

export function generateLMPData3(): MarketInsightPoint[] {
    const data: MarketInsightPoint[] = [];
    const totalPoints = 24 * 12;

    let currentUVValue = Math.floor(Math.random() * 40) + 30;
    let uvStepDuration = Math.floor(Math.random() * 8) + 4;
    let uvStepCounter = 0;
    let lastPvValue = currentUVValue;

    for (let i = 0; i < totalPoints; i++) {
        if (uvStepCounter >= uvStepDuration) {
            currentUVValue = Math.floor(Math.random() * 40) + 30;
            uvStepDuration = Math.floor(Math.random() * 8) + 4;
            uvStepCounter = 0;
        }

        let pvChange = (Math.random() * 25) - 12.5;
        let pvValue = lastPvValue + pvChange;

        if (Math.random() < 0.07) {
            pvValue += Math.random() * 200;
        } else if (Math.random() < 0.03) {
            pvValue -= Math.random() * 150;
        }

        const uvInfluence = (currentUVValue - pvValue) * 0.2;
        pvValue += uvInfluence;
        pvValue = Math.max(0, pvValue);

        data.push({
            name: `Point ${i + 1}`,
            uv: Math.floor(currentUVValue),
            pv: Math.floor(pvValue),
        });

        lastPvValue = pvValue;
        uvStepCounter++;
    }

    return data;
}

export function generateBiaxialLineChartData(numPoints: number) {
    const data = [];
    let currentUV = Math.random() * 20 + 2.5; // Initialize UV between 2.5 and 22.5
    let currentPV = Math.random() * 80 + 10; // Initialize PV between 10 and 90
    
    // Add momentum variables for smoother transitions
    let uvMomentum = 0;
    let pvMomentum = 0;

    for (let i = 0; i < numPoints; i++) {
        // Calculate target values with very small random changes
        const uvTarget = currentUV + (Math.random() - 0.5) * 0.5; // Reduced from 2 to 0.5
        const pvTarget = currentPV + (Math.random() - 0.5) * 1; // Reduced from 5 to 1

        // Apply momentum for smoother transitions
        uvMomentum = uvMomentum * 0.9 + (uvTarget - currentUV) * 0.1;
        pvMomentum = pvMomentum * 0.9 + (pvTarget - currentPV) * 0.1;

        // Add boundary avoidance
        if (currentUV < 5) {
            uvMomentum += 0.1; // Push away from lower boundary
        } else if (currentUV > 20) {
            uvMomentum -= 0.1; // Push away from upper boundary
        }

        if (currentPV < 15) {
            pvMomentum += 0.2; // Push away from lower boundary
        } else if (currentPV > 85) {
            pvMomentum -= 0.2; // Push away from upper boundary
        }

        // Update current values with momentum
        currentUV += uvMomentum;
        currentPV += pvMomentum;

        // Occasional spikes (3% chance, reduced from 5%)
        if (Math.random() < 0.03) {
            if (Math.random() < 0.5) {
                pvMomentum += Math.random() * 15; // Reduced spike magnitude
            } else {
                pvMomentum -= Math.random() * 10; // Reduced spike magnitude
            }
        }

        // Keep values within bounds with softer clamping
        currentUV = Math.max(2, Math.min(23, currentUV));
        currentPV = Math.max(5, Math.min(95, currentPV));

        data.push({
            name: `Point ${i + 1}`,
            uv: Math.floor(currentUV * 100) / 100, // Round to 2 decimal places
            pv: Math.floor(currentPV * 100) / 100, // Round to 2 decimal places
        });
    }

    return data;
}

export function generateLoadData(numPoints: number) {
    const data = [];
    
    for (let i = 0; i < numPoints; i++) {
        // Normalize x to be between 0 and 1:
        // i/(numPoints-1) gives us a value from 0 to 1
        const x = (i / (numPoints - 1))*2 -0.25;
        
        // Calculate the base curve value
        const curveValue = Math.pow(x + 0.2, 5) - Math.pow(x - 1, 4) - Math.pow(x, 2) - x + 2;
        
        // Scale and offset the curve value to ensure it's always positive (0-100)
        const scaledCurve = (curveValue + 6) * 12; // Increased offset to 6 and adjusted scaling to 12
        
        // Generate fuzzy variations for uv and pv
        // Add small random variations to every point
        const uvVariation = (Math.random() - 0.5) * 8; // Smaller range of variation
        const pvVariation = (Math.random() - 0.5) * 15; // Slightly larger range for pv
        
        let uv = scaledCurve * 0.7 + uvVariation; // Base value at 70% of curve plus variation
        let pv = scaledCurve + pvVariation; // Base value at 50% of curve plus variation
        
        // Ensure values don't go below 0
        uv = Math.max(0, uv);
        pv = Math.max(0, pv);
        
        data.push({
            name: `Point ${i + 1}`,
            uv: Math.floor(uv),
            pv: Math.floor(pv),
            amt: Math.floor(scaledCurve)
        });
    }
    
    return data;
} 